import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
} from "@coreui/react";
import { Spinner } from "react-bootstrap";
import { getItem } from "../api/generics";
import {
  documentLabels,
  documentListPaths,
  statusLabels,
} from "../models/document-dashboard";
import { formatUTCDateTime } from "../utils/dates";
import { SUCCESS } from "../utils/constants/tags";

interface DocumentRow {
  id: number;
  type: string;
  taxPayerId: number;
  taxPayerName: string;
  number: string;
  issuedAt: string;
  status: string;
  cdc: string | null;
}
interface DocumentList {
  items: DocumentRow[];
  count: number;
  page: number;
  pageSize: number;
}
const fields: Record<string, string> = {
  invoices: "invoice",
  credit_notes: "creditNote",
  debit_notes: "debitNote",
  remission_notes: "remissionNote",
  auto_invoices: "autoInvoice",
  receipts: "receipt",
};
const metrics: Record<string, string> = {
  pending: "Emitidos este mes pendientes de procesamiento",
  errors: "Emitidos este mes con errores",
};

// Dashboard results link to the regular document lists for history and actions.
const TaxPayerDocuments = ({
  taxPayerId,
}: {
  taxPayerId: number | undefined;
}) => {
  const { documentType } = useParams<{ documentType: string }>();
  const location = useLocation();
  const history = useHistory();
  const params = new URLSearchParams(location.search);
  const metric = params.get("metric") ?? "pending";
  const documentId = params.get("document_id");
  const [data, setData] = useState<DocumentList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [detail, setDetail] = useState<Record<string, any> | null>(null);
  const [detailError, setDetailError] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const validType =
    documentType === "all" ||
    Object.prototype.hasOwnProperty.call(documentLabels, documentType);
  const prefix = Object.prototype.hasOwnProperty.call(fields, documentType)
    ? fields[documentType]
    : undefined;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    setData(null);
    if (!validType) {
      setError(true);
      setLoading(false);
      return;
    }
    const query = new URLSearchParams(location.search);
    query.delete("document_id");
    query.set("type", documentType === "all" ? "" : documentType);
    query.set("tax_payer_id", String(taxPayerId));
    getItem<DocumentList>(
      "/stats/dashboard/documents/",
      new Map(query.entries()),
    )
      .then((response) => {
        if (!active) return;
        if (response.status !== SUCCESS || !response.data)
          throw new Error("documents");
        setData(response.data);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [documentType, location.search, validType, refresh, taxPayerId]);

  useEffect(() => {
    let active = true;
    setDetail(null);
    setDetailError(false);
    if (!documentId || !prefix) return;
    if (!/^\d+$/.test(documentId)) {
      setDetailError(true);
      return;
    }
    getItem<Record<string, any>>(
      `/stats/dashboard/documents/${documentType}/${documentId}/`,
      new Map([["tax_payer_id", String(taxPayerId)]]),
    )
      .then((response) => {
        if (!active) return;
        if (
          response.status !== SUCCESS ||
          !response.data ||
          response.data.taxPayerId !== taxPayerId
        )
          throw new Error("document");
        setDetail(response.data);
      })
      .catch(() => {
        if (active) setDetailError(true);
      });
    return () => {
      active = false;
    };
  }, [documentId, documentType, prefix, taxPayerId, location.search]);

  const closeDetail = () => {
    const query = new URLSearchParams(location.search);
    query.delete("document_id");
    history.replace({ pathname: location.pathname, search: query.toString() });
  };
  const pageLink = (page: number) => {
    const query = new URLSearchParams(location.search);
    query.set("page", String(page));
    query.delete("document_id");
    return `${location.pathname}?${query}`;
  };
  const documentLink = (document: DocumentRow) => {
    const query = new URLSearchParams();
    query.set("document_id", String(document.id));
    return `${documentListPaths[document.type]}?${query}`;
  };
  const detailRows = detail
    ? [
        ["Contribuyente", detail.taxPayerSocialName],
        ["RUC", detail.taxPayerRuc],
        [
          "Número",
          [
            detail.establishmentCode,
            detail.dispatchPointIdentifier,
            detail[`${prefix}Number`],
          ]
            .filter(Boolean)
            .join("-"),
        ],
        [
          "Fecha de emisión",
          detail[`${prefix}Date`]
            ? formatUTCDateTime(new Date(detail[`${prefix}Date`])) +
              " (Paraguay)"
            : null,
        ],
        ["Estado actual", statusLabels[detail.status] ?? detail.status],
        ["CDC", detail.cdc],
        [
          "Cliente / proveedor",
          detail.clientSocialName ??
            detail.providerSocialName ??
            detail.providerName,
        ],
        [
          "Monto",
          detail.amount != null
            ? `${Number(detail.amount).toLocaleString("es-PY")} ${detail.currency ?? ""}`
            : null,
        ],
        ["Código SIFEN", detail.setResponseCode],
        ["Respuesta SIFEN", detail.setResponseDescription],
        ["Error", detail.setError],
      ]
    : [];

  return (
    <>
      <CCard>
        <CCardBody>
          <Link to="/dashboard/">← Volver al resumen</Link>
          <div className="d-flex flex-wrap justify-content-between align-items-center mt-3 mb-3">
            <div>
              <h1 className="h3">
                {documentType === "all"
                  ? "Todos los documentos"
                  : (documentLabels[documentType] ?? "Tipo inválido")}
              </h1>
              <p className="text-muted mb-0">
                {metrics[metric] ?? "Período inválido"} · Hora de Paraguay
              </p>
            </div>
            <CButton
              color="primary"
              disabled={loading}
              onClick={() => setRefresh((value) => value + 1)}
            >
              Actualizar
            </CButton>
          </div>
          {params.get("error_key") && (
            <CAlert color="warning">
              Error: {params.get("error_key")} ·{" "}
              {statusLabels[params.get("error_status") ?? ""]}
            </CAlert>
          )}
          {error && (
            <CAlert color="danger">
              No se pudieron cargar los documentos. Verifique los filtros e
              intente actualizar.
            </CAlert>
          )}
          {loading ? (
            <div className="text-center py-5" role="status">
              <Spinner animation="border" />
              <span className="sr-only">Cargando documentos</span>
            </div>
          ) : (
            data && (
              <>
                <p className="text-muted small">
                  {data.count.toLocaleString("es-PY")} documento(s). Seleccione
                  un número para abrirlo en su lista y consultar el historial o
                  usar sus acciones.
                </p>
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Tipo / número</th>
                        <th>Contribuyente</th>
                        <th>Emisión (Paraguay)</th>
                        <th>Estado actual</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.items.map((document) => (
                        <tr key={`${document.type}:${document.id}`}>
                          <td>
                            <div className="small text-muted">
                              {documentLabels[document.type]}
                            </div>
                            <Link to={documentLink(document)}>
                              {document.number || `#${document.id}`}
                            </Link>
                          </td>
                          <td>{document.taxPayerName}</td>
                          <td>
                            {formatUTCDateTime(new Date(document.issuedAt))}
                          </td>
                          <td>
                            {statusLabels[document.status] ?? document.status}
                          </td>
                        </tr>
                      ))}
                      {!data.items.length && (
                        <tr>
                          <td
                            colSpan={4}
                            className="text-center text-muted py-4"
                          >
                            No hay documentos para esta selección.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <nav
                  className="d-flex justify-content-between align-items-center"
                  aria-label="Paginación de documentos"
                >
                  {data.page > 1 ? (
                    <Link to={pageLink(data.page - 1)}>← Anterior</Link>
                  ) : (
                    <span />
                  )}
                  <span className="small text-muted">
                    Página {data.page} de{" "}
                    {Math.max(1, Math.ceil(data.count / data.pageSize))}
                  </span>
                  {data.page * data.pageSize < data.count ? (
                    <Link to={pageLink(data.page + 1)}>Siguiente →</Link>
                  ) : (
                    <span />
                  )}
                </nav>
                {metric === "errors" && (
                  <p className="text-muted small mt-3 mb-0">
                    Solo se muestran documentos emitidos este mes cuyo estado
                    actual es de error.
                  </p>
                )}
                {metric === "pending" && (
                  <p className="text-muted small mt-3 mb-0">
                    Se muestran documentos emitidos este mes cuyo estado actual
                    es pendiente, generado, subido a SIFEN o con una solicitud
                    en curso; para recibos, pendiente o con anulación
                    solicitada.
                  </p>
                )}
              </>
            )
          )}
        </CCardBody>
      </CCard>
      <CModal
        show={Boolean(documentId && prefix)}
        onClose={closeDetail}
        size="lg"
      >
        <CModalHeader closeButton>
          <CModalTitle>
            {documentLabels[documentType]} · Documento #{documentId}
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          {detailError ? (
            <CAlert color="danger">No se pudo cargar el documento.</CAlert>
          ) : detail ? (
            <dl className="row">
              {detailRows
                .filter(
                  ([, value]) =>
                    value !== null && value !== undefined && value !== "",
                )
                .map(([label, value]) => (
                  <div className="col-12 mb-3" key={label}>
                    <dt>{label}</dt>
                    <dd className="mb-0" style={{ overflowWrap: "anywhere" }}>
                      {String(value)}
                    </dd>
                  </div>
                ))}
            </dl>
          ) : (
            <div className="text-center py-4" role="status">
              <Spinner animation="border" />
              <span className="sr-only">Cargando detalle</span>
            </div>
          )}
        </CModalBody>
      </CModal>
    </>
  );
};

const DashboardDocumentsPage = () => {
  const taxPayerId = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer.id,
  );
  return <TaxPayerDocuments key={taxPayerId} taxPayerId={taxPayerId} />;
};
export default DashboardDocumentsPage;
